import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, ExternalLink, Code, Mail, Github } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { Button } from '../ui/button';

const Hero = () => {
  const { personalInfo } = useAppContext();
  const [typedText, setTypedText] = useState('');
  const fullText = "I build innovative solutions through code.";

  useEffect(() => {
    let charIndex = 0;
    const typingInterval = setInterval(() => {
      if (charIndex <= fullText.length) {
        setTypedText(fullText.substring(0, charIndex));
        charIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, 100);

    return () => clearInterval(typingInterval);
  }, []);

  const scrollToProjects = () => {
    const projectsSection = document.getElementById('projects');
    if (projectsSection) {
      window.scrollTo({
        top: projectsSection.offsetTop - 80,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center py-20 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-20"></div>
      <div className="absolute inset-0 spotlight"></div>
      
      <div className="container mx-auto px-4 z-10">
        <div className="flex flex-col items-center justify-center gap-8">
          {/* Avatar image removed for a cleaner home screen */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="md:w-1/2 lg:w-2/5 text-center md:text-left"
          >
            <span className="text-primary terminal-text">Hello World! I am</span>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-4xl md:text-6xl font-bold mb-4 glitch terminal-text"
              data-text={personalInfo.name}
            >
              {personalInfo.name}
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-xl md:text-2xl text-foreground/80 mb-8 h-8"
            >
              {typedText}
              <span className="cursor"></span>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="mb-8 terminal-text flex justify-center md:justify-start"
            >
              <div className="code-block inline-block text-left vscode-theme">
                {/* Removed vscode-tabs div that contained the filename */}
                <span className="code-line">
                  <span className="code-keyword">const</span>{' '}
                  <span className="code-variable">Software Developer</span> <span className="text-white">=</span> {'{'}
                </span>
                
                <span className="code-line pl-4">
                  <span className="code-variable">major</span><span className="text-white">:</span>{' '}
                  <span className="code-string">"Computer Science"</span>,
                </span>
                
                <span className="code-line pl-4">
                  <span className="code-variable">focus</span><span className="text-white">:</span>{' '}
                  <span className="code-string">"Backend Development"</span>,
                </span>
                
                <span className="code-line pl-4">
                  <span className="code-variable">skills</span><span className="text-white">:</span> [
                  <span className="code-string">"Python"</span>,{' '}
                  <span className="code-string">"PHP"</span>,{' '}
                  <span className="code-string">"Team management"</span>]
                </span>
                
                <span className="code-line">{'}'}</span>
                <div className="vscode-line-numbers text-gray-600 absolute left-2 top-8 text-xs text-right opacity-60">
                  <div>1</div>
                  <div>2</div>
                  <div>3</div>
                  <div>4</div>
                  {/* <div>5</div> */}
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex flex-col sm:flex-row items-center sm:items-start justify-center md:justify-start gap-4"
            >
              <Button onClick={scrollToProjects} className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto">
                View Projects
                <ArrowDown className="ml-2 h-4 w-4" />
              </Button>
              
              <div className="flex items-center gap-4 mt-4 sm:mt-0">
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full border-primary/50 hover:bg-primary/10"
                  onClick={() => window.open(personalInfo.socialLinks.github, "_blank")}
                >
                  <Github className="h-5 w-5" />
                </Button>
                
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full border-primary/50 hover:bg-primary/10"
                  onClick={() => window.location.href = `mailto:${personalInfo.email}`}
                >
                  <Mail className="h-5 w-5" />
                </Button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      
    </section>
  );
};

export default Hero;
